import api from "./axios";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/patients`;
const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;

export const formatPatientPayload = (form) => ({
  firstName: form.firstName,
  lastName: form.lastName,
  dateOfBirth: form.dateOfBirth,
  email: form.email,
  phoneNumber: form.phoneNumber,
  addressLine: form.addressLine,
  city: form.city,
  state: form.state,
  country: form.country,
  postalCode: form.postalCode,
  documentType: form.documentType,
  documentCategory: form.documentCategory,
});

//For signup
export const signUpPatient = async (patientForm) => {
  const formData = new FormData();

  const patientPayload = formatPatientPayload(patientForm);
  formData.append("patient", JSON.stringify(patientPayload));

  if (patientForm.documentFile) {
    formData.append("documentFile", patientForm.documentFile);
  }

  const response = await api.post(`${API_BASE_URL}/signup`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

/**
 * Normalize/format payload to match backend DTO expectations.
 */
export const formatPatientPreferencesSavePayload = (form) => ({
  patientId: form.patientId,
  specialty: form.specialty ?? null,
  city: form.city ?? null,
  state: form.state ?? null,

  planIds: Array.isArray(form.insuranceIds)
    ? form.insuranceIds
    : form.insuranceIds
      ? [String(form.insuranceIds)]
      : [],

  // ✅ send language *names* array
  languageNames: Array.isArray(form.languageNames)
    ? form.languageNames
    : form.languageNames && form.languageNames.trim() !== ""
      ? [String(form.languageNames)] // ✅ Wrap string in array
      : [],

  ethnicity: form.ethnicity ? String(form.ethnicity).toUpperCase() : null,
  gender: form.gender ? String(form.gender).toUpperCase() : null,

  // ✅ years of experience range (min/max)
  minYearsOfExperience:
    form.minYearsOfExperience !== "" &&
    form.minYearsOfExperience !== null &&
    form.minYearsOfExperience !== undefined
      ? Number(form.minYearsOfExperience)
      : null,
  maxYearsOfExperience:
    form.maxYearsOfExperience !== "" &&
    form.maxYearsOfExperience !== null &&
    form.maxYearsOfExperience !== undefined
      ? Number(form.maxYearsOfExperience)
      : null,
});

//For updating
export const formatPatientPreferencesPayload = (form) => ({
  specialty: form.specialty ?? null,
  city: form.city ?? null,
  state: form.state ?? null,

  planIds: Array.isArray(form.insuranceIds)
    ? form.insuranceIds
    : form.insuranceIds
      ? [String(form.insuranceIds)]
      : [],

  languageNames: Array.isArray(form.languageNames)
    ? form.languageNames
    : form.languageIds && Array.isArray(form.languageIds)
      ? form.languageIds
      : [],

  ethnicity: form.ethnicity ? String(form.ethnicity).toUpperCase() : null,
  gender: form.gender ? String(form.gender).toUpperCase() : null,

  minYearsOfExperience:
    form.minYearsOfExperience !== "" &&
    form.minYearsOfExperience !== null &&
    form.minYearsOfExperience !== undefined
      ? Number(form.minYearsOfExperience)
      : null,
  maxYearsOfExperience:
    form.maxYearsOfExperience !== "" &&
    form.maxYearsOfExperience !== null &&
    form.maxYearsOfExperience !== undefined
      ? Number(form.maxYearsOfExperience)
      : null,
});

export const updatePatient = async (id, patientForm) => {
  const formData = new FormData();

  const patientPayload = formatPatientPayload(patientForm);
  formData.append("patient", JSON.stringify(patientPayload));

  if (patientForm.documentFile) {
    formData.append("documentFile", patientForm.documentFile);
  }

  const response = await api.put(`${API_BASE_URL}/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const getPatient = async (id) => {
  const response = await api.get(`${API_BASE_URL}/${id}`);
  return response.data;
};

export const deletePatient = async (id, patientId) => {
  await api.delete(`${API_BASE_URL}/${id}`, {
    params: { patientId },
  });
};

// Use helper method
export const savePatientPreferences = async (preferences) => {
  try {
    const response = await api.post("/api/v1/patient-preferences", preferences);
    return response.data;
  } catch (error) {
    console.error("Error saving patient preferences:", error);
    throw error;
  }
};

// ✅ Update patient preference
export const updatePatientPreference = async (patientId, form) => {
  const payload = formatPatientPreferencesPayload(form);
  const resp = await api.put(
    `/api/v1/patient-preferences/${patientId}`,
    payload,
  );
  return resp.data;
};

export const getPatientPreference = async (id) => {
  try {
    const response = await api.get(`${BASE_URL}/patient-preferences/${id}`);
    return response.data;
  } catch (err) {
    if (err.response?.status === 404) {
      // No preferences yet — return a sensible default
      return null; // or { specialty: null, ... } if you prefer
    }
    throw err; // other errors should still bubble up
  }
};

// ✅ Delete preference
export const deletePatientPreference = async (patientId) => {
  await api.delete(`/api/v1/patient-preferences/${patientId}`);
};

// Get matches (requires auth)
export const getPatientMatches = async (id, minScore) => {
  const response = await api.get(`/api/v1/matches/patient/${id}`, {
    params: minScore != null ? { minScore } : undefined,
  });
  return response.data;
};

//Delete matches
export const deletePatientMatches = async (patientId) => {
  await api.delete(`${BASE_URL}/matches/${patientId}`);
};

//Delete specific provider from match table
export const deletePatientMatch = async (patientId, providerId) => {
  await api.delete(`${BASE_URL}/matches/${patientId}/${providerId}`);
};

//For updating Patient profile information
export const getPatientProfile = async (patientId) => {
  const { data } = await api.get(`${API_BASE_URL}/${patientId}/profile`);
  return data;
};

export const updatePatientProfile = async (patientId, payload) => {
  // payload shape example:
  // { patientUpdate: {...}, insurances: [...], removeInsuranceIds: [...] }
  const { data } = await api.put(
    `${API_BASE_URL}/${patientId}/profile`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return data;
};

/*
 * For Phase 3 getting
 * Signing up with create account information
 */

// OPTIONAL: simple passthrough, assuming insuranceForm already matches PatientInsuranceDTO
export const formatPatientInsurancePayload = (insuranceForm) => ({
  ...insuranceForm,
});

/**
 * Phase 3: BASIC_ACCOUNT → create Patient profile for current logged-in user
 *
 * - Requires JWT token
 * - patientForm: same shape as signUpPatient
 * - insuranceForm: optional, should match PatientInsuranceDTO shape if provided
 * - documentFile: optional (patient document)
 * - insuranceCardFile: optional (insurance card upload)
 */
export const createPatientAccountForCurrentUser = async (
  patientForm,
  {
    insuranceForm = null,
    documentFile = null,
    insuranceCardFile = null,
    token, // pass in keycloak token from your auth layer
  } = {},
) => {
  const formData = new FormData();

  const patientPayload = formatPatientPayload(patientForm);
  formData.append("patient", JSON.stringify(patientPayload));

  if (insuranceForm) {
    const insurancePayload = formatPatientInsurancePayload(insuranceForm);
    formData.append("insurance", JSON.stringify(insurancePayload));
  }

  if (documentFile) {
    formData.append("documentFile", documentFile);
  }

  if (insuranceCardFile) {
    formData.append("insuranceCard", insuranceCardFile);
  }

  const headers = {
    "Content-Type": "multipart/form-data",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await api.post(
    `${API_BASE_URL}/me/create-account`,
    formData,
    { headers },
  );

  return response.data; // Patient (from aggregate.patient())
};
